import React, { useState, useMemo, useEffect, useRef, useCallback, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../../../components/Layout';
import { ModularPatientDataContext } from '../../../context/ModularPatientDataContext';
import { useModularApprovalTracking } from '../../../hooks/useModularApprovalTracking';
import { usePendingApprovals } from '../../../hooks/usePendingApprovals';
import { finalizeModularSectionReview } from '../../../utils/sectionReviewFinalization';
import { computeUnpersistedApprovals } from '../../../utils/approvalStateUtils';
import { toast } from 'react-toastify';
import { useFloatingActionPanel } from './components/floatingPanel/useFloatingActionPanel';
import { FloatingActionPanel } from './components/floatingPanel/FloatingActionPanel';
import { InitialWarningDialog } from './components/InitialWarningDialog';
import WarningBar from './components/WarningBar';
import DocumentReviewHeader from './components/DocumentReviewHeader';
import { decodeHtmlEntities } from './functions/htmlEncoders';
import { handlePlainTextPaste, suppressFormattingShortcuts } from './functions/contentEditableUtils';
import { renderModelInput } from '../../../utils/renderModelInput';
import { isAutismPatientFemale } from '../../../utils/conditionChecks';
import { useAuth } from '../../../hooks/useAuth';
import { useCanEditStage } from '../../../hooks/useCanEditStage';
import { STAGE_TO_CLINICIAN_KEY } from '../../../constants/adultAutismStages';
import useWorkflowConfig from '../../../hooks/useWorkflowConfig';
import { appendReportAuditEntry } from '../../../utils/reportAuditHelpers';
import { TriggerRenderAutismDocTemplate } from '../../../utils/TriggerRenderAutismDocTemplate';
import { rerenderAutismPatientReportIfStale } from '../../../utils/autoAutismPatientReport';
import { db, auth } from '../../../firebase';
import { doc as firestoreDoc, updateDoc, deleteField, serverTimestamp } from 'firebase/firestore';
import { getPatientReportContentPathSegments } from '../../../constants/adultAutismFirestorePaths';
import './styles/ReviewDocuments.css';
import {
  emitParagraphApproval,
  emitParagraphEdited,
  emitParagraphReset,
} from '../../../analytics/reportEvents';

/**
 * ReviewDocumentCI_Autism — Autism Clinical Interview review document page.
 *
 * This component mirrors the ADHD RD_CI.js in terms of UI behavior (floating action
 * panel, side panel with AI inputs, paragraph-level approve/edit/skip/reset workflow,
 * scroll-to-next-unreviewed, progress header) but differs architecturally:
 *
 * 1. DATA SOURCE: Reads from ModularPatientDataContext.reportContent.history
 *    (the subcollection doc) rather than the monolithic patientDataModel.
 *    The history doc contains { outputs, inputs, approvedParagraphs, editHistory }.
 *
 * 2. APPROVAL TRACKING: Uses useModularApprovalTracking which accepts
 *    { docStructure, reportDataOutputs, reportDataApproved } directly, since the
 *    modular architecture passes these from the subcollection doc rather than
 *    deriving them from a nested module-number-based path.
 *
 * 3. FIRESTORE WRITES: All approval, edit, and reset writes target the subcollection
 *    document at patients/{uid}/adult_autism/assessment_001/reportContent/history
 *    using targeted per-key updateDoc calls (no read-modify-write transaction, to
 *    avoid document-version contention on slow networks). The path is built via
 *    getPatientReportContentPathSegments(patientId, 'history').
 *
 * 4. DOCUMENT RENDERING: On exit, when the section is fully approved and dirty,
 *    we trigger TriggerRenderAutismDocTemplate (the modular counterpart to ADHD's
 *    TriggerRenderDocTemplate) to render the History PDF via the generic
 *    renderAndStoreAutismDocument Cloud Function. There is still no unified
 *    patient-report / cover-page re-render (those remain ADHD-only for now).
 *
 * 5. NAVIGATION: Exits to /patient/{id}/adult-autism/documents instead of the
 *    ADHD /patient/{id}/documents path.
 *
 * 6. MINIMAL WORKFLOW CONFIG: Autism is v2-only; no feature flags or version
 *    checks. The one per-patient conditional is the CI Risk Assessment gate
 *    (ciFlow.riskAssessment) — when disabled, the Risk Assessment paragraph
 *    group is omitted from the review structure (see docStructure below).
 */
export default function ReviewDocumentCI_Autism() {
  const { patientId } = useParams();

  // Module identifiers — used for audit entries and Firestore field naming
  const MODULE_PREFIX_CODE = 'history';
  const MODULE_TITLE = 'History';
  const navigate = useNavigate();

  // Pull data from the modular subcollection-based context rather than the
  // monolithic PatientDataContext used by ADHD. assessmentStages is used to
  // determine patient gender; reportContent.history holds outputs/inputs/approvals.
  const {
    header,
    reportContent,
    assessmentStages,
    updateReportContent,
    refreshReportContent,
    clearPatientData,
    fetchPatientData,
    loadedPatientId,
  } = useContext(ModularPatientDataContext);

  const { claims } = useAuth();

  // Workflow config — the only per-patient conditional in this v2 page. When the
  // Risk Assessment stage is disabled (ciFlow.riskAssessment: 'disabled', e.g.
  // TOC autism) the Risk Assessment paragraph group is omitted from the review
  // structure so reviewers don't see an empty "Risk Assessment > Summary" row.
  // Absent -> enabled, so legacy patients still review their risk paragraph.
  const wf = useWorkflowConfig(header, claims);

  // --------------------------------------------------------------------------
  // Edit permission gate
  // --------------------------------------------------------------------------
  // Only the clinician assigned as primary for the History module may review,
  // edit, and approve these AI paragraphs. Everyone else who can open the
  // Documents Dashboard (other module clinicians, org admins, DD supporting
  // clinicians) lands here read-only — they can browse the drafted paragraphs
  // and their AI inputs but cannot edit, approve, reset, or finalise. This is
  // defence-in-depth: the dashboard already hides the "Review AI Outputs"
  // button for non-primaries, but a deep link (or a bookmarked URL) must not
  // become an editing back door. `useCanEditStage` reads the same
  // `header.clinicians.clinicianCI` slot the dashboard uses.
  const canEdit = useCanEditStage(header, 'CI');

  // Name of the clinician who *is* allowed to review this module, surfaced in
  // the read-only banner so a non-assignee understands why they cannot edit.
  const assignedClinicianName =
    header?.clinicians?.[STAGE_TO_CLINICIAN_KEY.history]?.clinicianName;

  // Determine patient gender for conditional Women's Health section.
  // Uses the Autism-specific helper which reads from assessmentStages.history.data
  // rather than the ADHD patientDataModel.
  const patientIsFemale = useMemo(() => {
    return isAutismPatientFemale(assessmentStages);
  }, [assessmentStages]);

  // Force-fetch fresh data on mount to ensure we have the latest report content
  useEffect(() => {
    fetchPatientData(patientId);
  }, [patientId, fetchPatientData]);

  // --------------------------------------------------------------------------
  // Document Structure
  // --------------------------------------------------------------------------
  // Defines the mapping of section headings to paragraph entries with codes.
  // The codes correspond to keys in reportContent.history.outputs and .inputs.
  // Conditionally includes Women's Health based on patient gender.
  // Unlike ADHD, there is no Mental State Examination (MSE) section or
  // Formulation section — Autism CI covers different clinical domains.
  const docStructure = useMemo(() => {
    const base = {
      "Presenting Complaint": {
        "1": { title: "Referral Information", code: "asd_ci_COM_referral_information" },
        "2": { title: "History of Presenting Complaint", code: "asd_ci_COM_history_of_presenting_complaint" },
        "3": { title: "Impact on Daily Functioning", code: "asd_ci_COM_impact_on_daily_functioning" },
      },
      "Developmental History": {
        "1": { title: "Pre-natal and Birth", code: "asd_ci_DEV_pre_natal_and_birth" },
        "2": { title: "Early Years", code: "asd_ci_DEV_early_years" },
        "3": { title: "Childhood and Adolescence", code: "asd_ci_DEV_childhood_and_adolescence" },
        "4": { title: "Childhood Living Environment", code: "asd_ci_DEV_childhood_living_environment" },
        "5": { title: "Education", code: "asd_ci_DEV_education" },
        "6": { title: "Motor Coordination", code: "asd_ci_DEV_motor_coordination" },
      },
      "Personal and Social History": {
        "1": { title: "Current Living Situation", code: "asd_ci_SOC_current_living_situation" },
        "2": { title: "Relationships", code: "asd_ci_SOC_relationships" },
        "3": { title: "Employment", code: "asd_ci_SOC_employment" },
      },
      "Lifestyle History": {
        "1": { title: "Lifestyle Factors", code: "asd_ci_LIF_lifestyle_factors" },
        "2": { title: "Alcohol and Substance Use", code: "asd_ci_LIF_alcohol_and_substance_use" },
        "3": { title: "Habits", code: "asd_ci_LIF_habits" },
        "4": { title: "Driving", code: "asd_ci_LIF_driving" },
      },
      "Forensic History": {
        "1": { title: "Legal Issues", code: "asd_ci_FOR_legal_issues" },
        "2": { title: "Perpetrator History", code: "asd_ci_FOR_perpetrator_history" },
        "3": { title: "Victim History", code: "asd_ci_FOR_victim_history" },
      },
      "Family History": {
        "1": { title: "Health Conditions", code: "asd_ci_FAM_health_conditions" },
      },
      "Medical History": {
        "1": { title: "Health Conditions", code: "asd_ci_MED_health_conditions" },
        "2": { title: "Hospital Visits", code: "asd_ci_MED_hospital_visits" },
        "3": { title: "Medications", code: "asd_ci_MED_medications" },
        "4": { title: "Allergies", code: "asd_ci_MED_allergies" },
        "5": { title: "Vision and Hearing", code: "asd_ci_MED_vision_and_hearing" },
      },
      "Psychiatric History": {
        "1": { title: "Previous and Current Diagnoses", code: "asd_ci_PSY_diagnoses" },
        "2": { title: "Previous and Current Treatments", code: "asd_ci_PSY_treatments" },
        "3": { title: "Trauma and Risk History", code: "asd_ci_PSY_trauma_and_risk" },
      },
    };

    // Owl Centre Autism form profile: when active, remove sections/paragraphs
    // that were trimmed from the patient's seeded data and add the moved
    // Legal Issues paragraph under Personal and Social History.
    const hasFormProfile = wf.assessmentFormProfile === 'owlCentreAutismV1';
    if (hasFormProfile) {
      delete base["Forensic History"];
      delete base["Lifestyle History"]["4"]; // Driving
      base["Personal and Social History"]["4"] = {
        title: "History of Legal Issues",
        code: "asd_ci_SOC_history_of_legal_issues",
      };
    }

    // Risk Assessment is only part of the review structure when this patient's
    // workflow includes the Risk Assessment stage. When disabled (TOC autism) it
    // is never generated, so omitting the group keeps totalParagraphs correct and
    // avoids an empty review row.
    if (wf.isCIRiskAssessmentEnabled) {
      base["Risk Assessment"] = {
        "1": { title: "Summary", code: "asd_ci_RSK_summary" },
      };
    }

    // Women's Health: only shown when the patient is female AND the form
    // profile is not active (the profile unconditionally removes Women's
    // Health regardless of sex assigned at birth).
    if (patientIsFemale && !hasFormProfile) {
      base["Medical History"]["6"] = { title: "Women's Health", code: "asd_ci_MED_womens_health" };
    }

    return base;
  }, [patientIsFemale, wf.isCIRiskAssessmentEnabled, wf.assessmentFormProfile]);

  // --------------------------------------------------------------------------
  // Report Data Extraction
  // --------------------------------------------------------------------------
  // Extract outputs from the reportContent.history subcollection document.
  // In ADHD this was patientDataModel.dataModel['6_manageReports'].sections['1'].outputs;
  // here it's directly on the reportContent document keyed by stage ('history').
  const historyDoc = reportContent?.history;
  const REPORT_DATA_OUTPUTS = useMemo(
    () => historyDoc?.outputs || {},
    [historyDoc]
  );

  // The paragraph edit history, read from the SAME already-loaded section as the
  // outputs above. It is what lets the adoption numbers (how much of the AI draft
  // survived) be computed at the moment of an edit rather than by reading the
  // report back out of Firestore later - and because this object is already in
  // memory to render the page, it costs no additional read.
  //
  // In-memory means it can lag a write made elsewhere in the same instant. That
  // only matters for a paragraph's FIRST edit, and only if another session edited
  // it seconds earlier; once the earliest entry exists it stays the earliest
  // forever. The nightly reconciliation reads authoritative Firestore, so a
  // disagreement surfaces there rather than going unnoticed.
  const REPORT_EDIT_HISTORY = useMemo(() =>
    historyDoc?.editHistory || {},
    [historyDoc]);

  // --------------------------------------------------------------------------
  // Component State
  // --------------------------------------------------------------------------
  const [state, setState] = useState({
    selectedCode: null,
    textareaContent: null,
    isSidePanelOpen: false,
    modelInputsFirestore: null,
    clinicianInputs: null,
    clinicianDecisions: null,
    patientInputs: null,
    informantInputs: null,
  });

  const [isEditing, setIsEditing] = useState(false);

  // --------------------------------------------------------------------------
  // Approval Tracking
  // --------------------------------------------------------------------------
  // Uses the modular hook which accepts report data directly rather than
  // deriving it from patientId + moduleNumber like the ADHD useApprovalTracking.
  const {
    totalParagraphs, reviewedCodes, isFullyApproved,
    showWarning, addReviewedCode, removeReviewedCode,
  } = useModularApprovalTracking({
    docStructure,
    reportDataOutputs: REPORT_DATA_OUTPUTS,
    reportDataApproved: historyDoc?.approvedParagraphs,
  });

  // Tracks in-flight per-paragraph approval writes so Save & Exit can wait for
  // them to commit before deriving and persisting the section approval flag.
  const { track: trackApproval, settleAll: settlePendingApprovals } = usePendingApprovals();

  // --------------------------------------------------------------------------
  // Paragraph Click Handler
  // --------------------------------------------------------------------------
  // When a paragraph is clicked, open the side panel with the corresponding
  // AI inputs (clinician notes, decisions, patient and informant prefill).
  const handleParagraphClick = (event, code, content) => {
    setIsEditing(false);

    if (!state.modelInputsFirestore) {
      console.error('modelInputsFirestore is not populated');
      return;
    }

    if (code && state.modelInputsFirestore[code]) {
      setState(prevState => ({
        ...prevState,
        selectedCode: code,
        textareaContent: content || ' ',
        clinicianInputs: state.modelInputsFirestore[code]["Clinician Notes"],
        clinicianDecisions: state.modelInputsFirestore[code]["Clinician Decisions"],
        patientInputs: state.modelInputsFirestore[code]["Patient Prefill"],
        informantInputs: state.modelInputsFirestore[code]["Informant Prefill"],
        isSidePanelOpen: true,
      }));
      setTimeout(() => {
        safeUpdatePosition(code);
      }, 0);
    } else {
      console.error('No matching input found for the code:', code);
    }
  };

  // --------------------------------------------------------------------------
  // Section Component
  // --------------------------------------------------------------------------
  // Renders a single document section with its paragraphs. Filters out
  // paragraphs that have no generated content (empty or undefined in outputs).
  // Paragraphs are clickable, contentEditable when in edit mode, and styled
  // based on approval status.
  const Section = ({ title, paragraphs }) => {
    const availableParagraphs = Object.entries(paragraphs).filter(
      ([_, { code }]) => REPORT_DATA_OUTPUTS[code] !== undefined && REPORT_DATA_OUTPUTS[code] !== ""
    );

    if (availableParagraphs.length === 0) return null;

    return (
      <div className="mb-8">
        <h2 className="text-2xl font-bold mb-4 text-blue-500">{title}</h2>

        {availableParagraphs.map(([key, { title: subTitle, code }]) => {
          const content = REPORT_DATA_OUTPUTS[code];
          const decodedContent = decodeHtmlEntities(content);
          return (
            <div key={key} className="mb-4">
              {subTitle && (
                <h3 className="font-semibold mb-2 text-gray-900">
                  {subTitle}
                </h3>
              )}
              <div
                data-paragraph-container
                className={`
                relative text-sm font-medium p-2 rounded-lg
                transition-all duration-200
                ${isEditing && state.selectedCode === code
                    ? 'bg-amber-100 z-20'
                    : reviewedCodes.has(code)
                      ? 'bg-green-600/30 hover:bg-green-600/40 approved-paragraph'
                      : 'bg-violet-100 hover:bg-violet-200'
                  }
                ${state.selectedCode === code
                    ? 'ring ring-violet-800'
                    : ''
                  }
                ${!isEditing ? 'cursor-pointer' : ''}
              `}
                id={code}
                onClick={(e) => {
                  if (!isEditing) {
                    handleParagraphClick(e, code, decodedContent);
                  }
                }}
              >
                <p
                  ref={editableContentRef}
                  className="text-gray-700 leading-relaxed whitespace-pre-wrap"
                  contentEditable={isEditing && state.selectedCode === code}
                  onKeyDown={suppressFormattingShortcuts}
                  onInput={(e) => {
                    if (isEditing && state.selectedCode === code) {
                      let newContent = e.target.innerText;
                      newContent = newContent.replace(/\n/g, '\n');

                      if (REPORT_DATA_OUTPUTS) {
                        REPORT_DATA_OUTPUTS[code] = newContent;
                      }
                      safeUpdatePosition(code);
                    }
                  }}
                  onPaste={handlePlainTextPaste}
                  suppressContentEditableWarning={true}
                >
                  {decodedContent}
                </p>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  // --------------------------------------------------------------------------
  // Refs and Floating Panel
  // --------------------------------------------------------------------------
  const editableContentRef = useRef(null);
  const documentContainerRef = useRef(null);

  const { panelRef, panelPosition, safeUpdatePosition } = useFloatingActionPanel({
    containerRef: documentContainerRef,
    selectedCode: state.selectedCode,
    isEditing,
    topOffset: -15,
  });

  // --------------------------------------------------------------------------
  // Edit Handlers
  // --------------------------------------------------------------------------
  // handleStartEdit: Stores current content for revert, enables contentEditable,
  // focuses the paragraph, and repositions the floating panel.
  const handleStartEdit = () => {
    if (!canEdit) return;
    if (state.selectedCode) {
      setPreEditContent(prev => ({
        ...prev,
        [state.selectedCode]: REPORT_DATA_OUTPUTS[state.selectedCode] || '',
      }));
    }
    setIsEditing(true);
    setTimeout(() => {
      const paragraphElement = document.querySelector(`#${state.selectedCode} p`);
      if (paragraphElement) {
        paragraphElement.focus();
        const range = document.createRange();
        const selection = window.getSelection();
        range.selectNodeContents(paragraphElement);
        range.collapse(false);
        selection.removeAllRanges();
        selection.addRange(range);
      }
      safeUpdatePosition(state.selectedCode);
    }, 0);
  };

  // handleSaveEdit: Persists the edited paragraph to the reportContent/history
  // subcollection doc. Creates an edit history entry for audit purposes.
  // Unlike ADHD, writes to the subcollection doc rather than the monolithic patient doc.
  const handleSaveEdit = () => {
    if (!canEdit) {
      toast.error('Only the clinician assigned to the History module can edit these paragraphs.');
      return;
    }
    if (state.selectedCode) {
      const spanCode = state.selectedCode;
      const newValue = REPORT_DATA_OUTPUTS[spanCode];
      const previousValue = initialContent[spanCode] || '';

      if (newValue !== previousValue) {
        removeReviewedCode(state.selectedCode);
        setIsEditing(false);
        safeUpdatePosition(state.selectedCode);

        const editEntry = {
          clinicianId: auth.currentUser.uid,
          clinicianName: auth.currentUser.displayName,
          timestamp: serverTimestamp(),
          previousValue,
          newValue,
          editType: "manual",
        };
        const timestampKey = Date.now().toString();

        // Write to the reportContent/history subcollection document with a
        // targeted per-key updateDoc rather than a read-modify-write
        // runTransaction. A transaction commits with a document-version
        // precondition and, on slow 3G/4G, contends with concurrent writes and
        // fails ("the stored version does not match the required base version"),
        // silently discarding the edit. A plain updateDoc on distinct field
        // paths has no such precondition. Unlike ADHD (which writes to the
        // monolithic patients/{id} doc), Autism writes to the dedicated
        // subcollection doc at patients/{id}/adult_autism/assessment_001/
        // reportContent/history, so the fields live at the doc root (no section
        // prefix). See handleParagraphApproval for the same reasoning.
        const reportContentPath = getPatientReportContentPathSegments(patientId, 'history');
        const docRef = firestoreDoc(db, ...reportContentPath);

        const startedAt = performance.now();
        updateDoc(docRef, {
          // Persist the edited text
          [`outputs.${spanCode}`]: newValue,
          // Editing invalidates any prior approval
          [`approvedParagraphs.${spanCode}`]: deleteField(),
          // Append an audit entry under this paragraph's edit history
          [`editHistory.${spanCode}.${timestampKey}`]: editEntry,
          // Review metadata (mirrors the approval write)
          lastReviewedAt: serverTimestamp(),
          reviewedBy: auth.currentUser.uid,
          reviewedByName: auth.currentUser.displayName,
        }).then(() => {
          // Confirms the edit persisted AND the paragraph was un-approved
          // (removed from approvedParagraphs) in Firestore. Code-only, no PII.
          const elapsedMs = Math.round(performance.now() - startedAt);
          console.log(`[edit] saved & UNAPPROVED "${spanCode}" (content updated) in ${elapsedMs}ms`);
          // Analytics mirror of the edit that just persisted: paragraph code,
          // section and the two lengths only — never the wording itself.
          emitParagraphEdited({
            patientId,
            module: MODULE_PREFIX_CODE,
            docStructure,
            code: spanCode,
            prevText: previousValue,
            priorHistory: REPORT_EDIT_HISTORY[spanCode] || {},
            nextText: newValue,
          });
        }).catch(error => {
          console.error("Error updating paragraph on edit:", error);
          // Roll back the optimistic un-approval and surface the failure so
          // the clinician does not believe their edit was saved.
          addReviewedCode(state.selectedCode);
          toast.error(
            'Your edit did not save. Please try editing the paragraph again.',
            { toastId: 'paragraph-edit-save-error' }
          );
        });

        setSectionDirty(true);
      } else {
        setIsEditing(false);
        safeUpdatePosition(state.selectedCode);
      }
    }
  };

  // handleCancel: Reverts the paragraph content to its pre-edit state without
  // persisting anything to Firestore.
  const handleCancel = () => {
    if (state.selectedCode && preEditContent[state.selectedCode]) {
      if (REPORT_DATA_OUTPUTS) {
        REPORT_DATA_OUTPUTS[state.selectedCode] = preEditContent[state.selectedCode];
      }
    }
    setIsEditing(false);
    safeUpdatePosition(state.selectedCode);
  };

  // handleReset: Reverts paragraph content to its original LLM-generated value
  // (the value on page load), removes approval, and records a reset entry in
  // edit history.
  const handleReset = () => {
    if (!canEdit) {
      toast.error('Only the clinician assigned to the History module can reset these paragraphs.');
      return;
    }
    if (state.selectedCode && initialContent[state.selectedCode]) {
      const spanCode = state.selectedCode;
      const newValue = initialContent[state.selectedCode];
      const previousValue = REPORT_DATA_OUTPUTS[state.selectedCode];

      if (REPORT_DATA_OUTPUTS) {
        REPORT_DATA_OUTPUTS[state.selectedCode] = initialContent[state.selectedCode];
      }

      removeReviewedCode(state.selectedCode);

      const editEntry = {
        clinicianId: auth.currentUser.uid,
        clinicianName: auth.currentUser.displayName,
        timestamp: serverTimestamp(),
        previousValue,
        newValue,
        isReset: true,
      };
      const timestampKey = Date.now().toString();

      // Targeted per-key updateDoc (root fields on the reportContent/history
      // subcollection doc) instead of a read-modify-write runTransaction, which
      // contends on the doc version under slow 3G/4G and loses the reset. See
      // handleSaveEdit / handleParagraphApproval for the full rationale.
      const reportContentPath = getPatientReportContentPathSegments(patientId, 'history');
      const docRef = firestoreDoc(db, ...reportContentPath);

      const startedAt = performance.now();
      updateDoc(docRef, {
        // Restore the original generated text
        [`outputs.${spanCode}`]: newValue,
        // A reset also invalidates any prior approval
        [`approvedParagraphs.${spanCode}`]: deleteField(),
        // Record the reset in this paragraph's edit history
        [`editHistory.${spanCode}.${timestampKey}`]: editEntry,
        // Review metadata (mirrors the approval write)
        lastReviewedAt: serverTimestamp(),
        reviewedBy: auth.currentUser.uid,
        reviewedByName: auth.currentUser.displayName,
      }).then(() => {
        // Confirms the reset persisted AND the paragraph was un-approved
        // (removed from approvedParagraphs) in Firestore. Code-only, no PII.
        const elapsedMs = Math.round(performance.now() - startedAt);
        console.log(`[reset] reset & UNAPPROVED "${spanCode}" in ${elapsedMs}ms`);
        // Analytics mirror of the reset that just persisted (code only).
        emitParagraphReset({
          patientId,
          module: MODULE_PREFIX_CODE,
          docStructure,
          code: spanCode,
        });
      }).catch(error => {
        console.error("Error resetting paragraph:", error);
        // Roll back both the content and the approval state, then surface the
        // failure so the clinician knows the reset did not persist.
        if (REPORT_DATA_OUTPUTS) {
          REPORT_DATA_OUTPUTS[state.selectedCode] = previousValue;
        }
        addReviewedCode(state.selectedCode);
        toast.error(
          'The paragraph reset did not save. Please try again.',
          { toastId: 'paragraph-reset-save-error' }
        );
      });

      setIsEditing(false);
      setSectionDirty(true);
      safeUpdatePosition(state.selectedCode);
    }
  };

  // hasBeenModified: Checks if a paragraph's content differs from the original
  // value captured on page load. Used to show/hide the reset button.
  const hasBeenModified = (code) => {
    if (!code) return false;
    return REPORT_DATA_OUTPUTS[code] !== initialContent[code];
  };

  // --------------------------------------------------------------------------
  // Warning Dialogs and Initial Content
  // --------------------------------------------------------------------------
  const [showInitialWarning, setShowInitialWarning] = useState(true);
  const [initialContent, setInitialContent] = useState({});

  // Capture the initial content of each paragraph on first load so we can
  // detect modifications and support resets.
  useEffect(() => {
    if (REPORT_DATA_OUTPUTS && Object.keys(REPORT_DATA_OUTPUTS).length > 0) {
      const initialValues = {};
      Object.values(docStructure).forEach(section => {
        Object.values(section).forEach(({ code }) => {
          if (code && REPORT_DATA_OUTPUTS[code] !== undefined && REPORT_DATA_OUTPUTS[code] !== "") {
            initialValues[code] = REPORT_DATA_OUTPUTS[code];
          }
        });
      });
      setInitialContent(initialValues);
    }
  }, [REPORT_DATA_OUTPUTS, docStructure]);

  const [preEditContent, setPreEditContent] = useState({});

  const [isSaving, setIsSaving] = useState(false);
  const [sectionDirty, setSectionDirty] = useState(false);

  // --------------------------------------------------------------------------
  // Paragraph Approval (Firestore write)
  // --------------------------------------------------------------------------
  // Writes approval or revocation to the reportContent/history subcollection doc.
  // Unlike ADHD which targets patients/{id}.dataModel.6_manageReports.sections.1,
  // Autism writes directly to the reportContent subcollection document's
  // approvedParagraphs field.
  const handleParagraphApproval = async (code, isApproving) => {
    if (!canEdit) {
      toast.error('Only the clinician assigned to the History module can approve these paragraphs.');
      return;
    }
    try {
      const reportContentPath = getPatientReportContentPathSegments(patientId, 'history');
      const docRef = firestoreDoc(db, ...reportContentPath);

      // Targeted per-key write instead of a full read-modify-write transaction
      // on approvedParagraphs — avoids document-version contention when a
      // clinician approves many paragraphs quickly on slow 3G/4G links (see
      // RD_CI.js for the full rationale). `code` is a dot-free identifier and
      // the fields below are siblings, so there is no conflicting field-path
      // rejection.
      const updateData = {
        [`approvedParagraphs.${code}`]: isApproving
          ? {
              approvedAt: serverTimestamp(),
              approvedBy: auth.currentUser.uid,
              approvedByName: auth.currentUser.displayName,
              content: REPORT_DATA_OUTPUTS[code],
            }
          : deleteField(),
        lastReviewedAt: serverTimestamp(),
        reviewedBy: auth.currentUser.uid,
        reviewedByName: auth.currentUser.displayName,
      };

      // Timed, PII-free confirmation log (updateDoc resolves on server ack).
      const startedAt = performance.now();
      await updateDoc(docRef, updateData);
      const elapsedMs = Math.round(performance.now() - startedAt);
      console.log(
        `[approval] ${isApproving ? 'APPROVED' : 'UNAPPROVED'} "${code}" persisted to Firestore in ${elapsedMs}ms`
      );

      // Analytics mirror of the approval change that just persisted
      // (code only). Both directions share the review control that
      // wrote it, so both share this one call.
      emitParagraphApproval({
        patientId,
        module: MODULE_PREFIX_CODE,
        docStructure,
        code,
        assessmentType: 'autism',
        isApproving,
      });

      setSectionDirty(true);
    } catch (error) {
      console.error("Error updating paragraph approval:", error);
      // Re-throw so the caller can observe the failure. Swallowing the error
      // here made failed approval writes invisible: the paragraph stayed green
      // on screen with no persisted approval behind it — the proven cause of
      // reports finalising with missing / unapproved content.
      throw error;
    }
  };

  // --------------------------------------------------------------------------
  // Mark as Done (Approve + Advance)
  // --------------------------------------------------------------------------
  // Approves the currently selected paragraph, then scrolls to and selects the
  // next unreviewed paragraph. Same UX pattern as ADHD.
  const handleMarkAsDone = async () => {
    if (!canEdit) {
      toast.error('Only the clinician assigned to the History module can approve these paragraphs.');
      return;
    }
    if (!state.selectedCode) return;

    const paragraphElement = document.getElementById(state.selectedCode);
    if (paragraphElement) {
      try {
        // Track the in-flight write so Save & Exit awaits it before finalising.
        trackApproval(
          handleParagraphApproval(state.selectedCode, true).catch(error => {
            console.error('Error in handleParagraphApproval:', error);
            // Roll back the optimistic update and tell the clinician — a
            // silently failed approval is exactly how incomplete sections
            // used to slip through to the rendered report.
            removeReviewedCode(state.selectedCode);
            toast.error(
              'A paragraph approval did not save and has been returned to "needs review". Please approve it again.',
              { toastId: 'paragraph-approval-save-error' }
            );
          })
        );

        const allParagraphs = document.querySelectorAll('[data-paragraph-container]');
        const currentIndex = Array.from(allParagraphs).findIndex(p => p.id === state.selectedCode);
        const nextUnreviewed = Array.from(allParagraphs).find((p, index) =>
          index > currentIndex && !reviewedCodes.has(p.id)
        );

        addReviewedCode(state.selectedCode);

        if (nextUnreviewed && documentContainerRef.current) {
          const containerRect = documentContainerRef.current.getBoundingClientRect();
          const nextRect = nextUnreviewed.getBoundingClientRect();

          const scrollTop = documentContainerRef.current.scrollTop +
            (nextRect.top - containerRect.top) -
            containerRect.height / 3;

          documentContainerRef.current.scrollTo({
            top: scrollTop,
            behavior: 'smooth',
          });

          setTimeout(() => {
            const nextCode = nextUnreviewed.id;

            setState(prev => ({
              ...prev,
              selectedCode: nextCode,
              isSidePanelOpen: true,
              clinicianInputs: state.modelInputsFirestore[nextCode]?.["Clinician Notes"],
              clinicianDecisions: state.modelInputsFirestore[nextCode]?.["Clinician Decisions"],
              patientInputs: state.modelInputsFirestore[nextCode]?.["Patient Prefill"],
              informantInputs: state.modelInputsFirestore[nextCode]?.["Informant Prefill"],
            }));

            setTimeout(() => safeUpdatePosition(nextCode), 0);
          }, 300);
        } else {
          setState(prev => ({
            ...prev,
            selectedCode: null,
            isSidePanelOpen: false,
            clinicianInputs: null,
            clinicianDecisions: null,
            patientInputs: null,
            informantInputs: null,
          }));
        }
      } catch (error) {
        console.error('Error in handleMarkAsDone:', error);
      }
    }
  };

  // --------------------------------------------------------------------------
  // Skip (Advance without Approving)
  // --------------------------------------------------------------------------
  // Moves to the next unreviewed paragraph without approving the current one.
  const handleSkip = () => {
    if (!canEdit) return;
    if (!state.selectedCode) return;

    const paragraphElement = document.getElementById(state.selectedCode);
    if (paragraphElement) {
      const allParagraphs = document.querySelectorAll('[data-paragraph-container]');
      const currentIndex = Array.from(allParagraphs).findIndex(p => p.id === state.selectedCode);
      const nextUnreviewed = Array.from(allParagraphs).find((p, index) =>
        index > currentIndex && !reviewedCodes.has(p.id)
      );

      if (nextUnreviewed && documentContainerRef.current) {
        const containerRect = documentContainerRef.current.getBoundingClientRect();
        const nextRect = nextUnreviewed.getBoundingClientRect();

        const scrollTop = documentContainerRef.current.scrollTop +
          (nextRect.top - containerRect.top) -
          containerRect.height / 3;

        documentContainerRef.current.scrollTo({
          top: scrollTop,
          behavior: 'smooth',
        });

        setTimeout(() => {
          const nextCode = nextUnreviewed.id;

          setState(prev => ({
            ...prev,
            selectedCode: nextCode,
            isSidePanelOpen: true,
            clinicianInputs: state.modelInputsFirestore[nextCode]?.["Clinician Notes"],
            clinicianDecisions: state.modelInputsFirestore[nextCode]?.["Clinician Decisions"],
            patientInputs: state.modelInputsFirestore[nextCode]?.["Patient Prefill"],
            informantInputs: state.modelInputsFirestore[nextCode]?.["Informant Prefill"],
          }));

          setTimeout(() => safeUpdatePosition(nextCode), 0);
        }, 300);
      } else {
        setState(prev => ({
          ...prev,
          selectedCode: null,
          isSidePanelOpen: false,
          clinicianInputs: null,
          clinicianDecisions: null,
          patientInputs: null,
          informantInputs: null,
        }));
      }
    }
  };

  // --------------------------------------------------------------------------
  // Exit Review
  // --------------------------------------------------------------------------
  // Finalizes the review session: writes overall approval status to the
  // reportContent/history doc, records an audit entry, triggers PDF/DOCX
  // rendering when appropriate, then navigates to the Autism documents
  // dashboard.
  const handleExitReview = async () => {
    // Read-only viewers (not the History primary) can leave the page, but must
    // never run the finalise path: no approval/status writes, no audit entry,
    // and no PDF/patient-report render. Just drop cached data and navigate back
    // to the dashboard. Editing was already blocked upstream, so there is
    // nothing of theirs to persist here.
    if (!canEdit) {
      // Never act on assignment until the patient doc has loaded for THIS
      // patient. canEdit starts false and only becomes true once `header`
      // hydrates, so a legitimate assignee who clicks before that would
      // otherwise be treated as read-only and navigated away without
      // finalising. Once loaded, canEdit is authoritative.
      if (loadedPatientId !== patientId) {
        toast.error(
          'The review is still loading. Please wait a moment and try again.',
          { toastId: 'review-exit-not-loaded' }
        );
        return;
      }
      clearPatientData();
      navigate(`/patient/${patientId}/adult-autism/documents`);
      return;
    }

    setIsSaving(true);

    try {
      // Guard: never finalise against a document that has not fully loaded for
      // THIS patient. If reportContent is empty/stale the docStructure and the
      // approval denominator are wrong, which could either (a) write a false
      // "approved" flag with unapproved content behind it, or (b) revert a
      // previously-approved flag to false. Bail out and let the clinician retry.
      if (loadedPatientId !== patientId) {
        toast.error(
          'The review is still loading. Please wait a moment and try again.',
          { toastId: 'review-exit-not-loaded' }
        );
        setIsSaving(false);
        return;
      }

      // Wait for any optimistic per-paragraph approval writes to commit so the
      // finalisation below reads the true, current approval state from Firestore.
      await settlePendingApprovals();

      // Derive and persist the section approval flag transactionally from the
      // fresh document snapshot (see finalizeModularSectionReview). This replaces
      // the previous pattern of writing the client's optimistic isFullyApproved,
      // which could diverge from Firestore and finalise an incomplete report.
      const { isFullyApproved: approvedNow, outstandingCodes, wasFirstApproval } =
        await finalizeModularSectionReview({
          patientId,
          stageKey: 'history',
          flagPrefix: MODULE_PREFIX_CODE,
          docStructure,
        });

      // Reconcile the optimistic UI against the authoritative snapshot before
      // allowing the exit. Stay on the page when either:
      //   1. "Phantom approvals" exist — paragraphs shown green whose background
      //      write actually failed (rapid clicking can overload the per-document
      //      transaction queue), or
      //   2. the UI believed everything was approved but the snapshot found
      //      outstanding content (e.g. paragraphs generated after page load).
      // The affected paragraphs are reverted to needs-review and the section
      // doc is re-fetched so the page reflects reality.
      const unpersisted = computeUnpersistedApprovals(reviewedCodes, outstandingCodes);
      if (!approvedNow && (isFullyApproved || unpersisted.length > 0)) {
        unpersisted.forEach(removeReviewedCode);
        await refreshReportContent(patientId, 'history');
        toast.error(
          unpersisted.length > 0
            ? `${unpersisted.length} paragraph approval${unpersisted.length === 1 ? ' did' : 's did'} not save. `
              + 'The affected paragraphs have been returned to "needs review" — please approve them again.'
            : 'Some paragraphs still need review before this section can be completed. '
              + 'The page has been refreshed to show them.',
          { toastId: 'review-exit-unsaved' }
        );
        return;
      }

      // Fire-and-forget audit entry for report approval event tracking. The
      // first-occurrence flag comes from the transaction's snapshot-derived
      // result, not stale React context.
      appendReportAuditEntry(patientId, {
        module: MODULE_PREFIX_CODE,
        eventType: 'approved',
        isFirstOccurrence: wasFirstApproval,
      });

      // Kick off PDF/DOCX rendering of the History report. We only (re)render
      // when the section is fully approved (snapshot-derived) AND something
      // changed this session (sectionDirty), matching the ADHD gating — this
      // avoids regenerating an already-current document on a no-op exit.
      if (approvedNow && sectionDirty) {
        TriggerRenderAutismDocTemplate({
          patientId,
          stageKey: MODULE_PREFIX_CODE,
          clinicianId: auth.currentUser.uid,
        }).catch((err) => console.error('Error triggering autism history render:', err));
      }

      // Auto-finalise the combined Patient Report. Called unconditionally (fire-and-forget):
      // the helper reads fresh Firestore state and only (re)renders when all five section
      // reports are approved AND the patient report is stale — mirroring the ADHD flow, so
      // there is no manual "Generate" button. Safe even on a no-op exit.
      rerenderAutismPatientReportIfStale({ patientId, clinicianId: auth.currentUser.uid })
        .catch((err) => console.error('Autism PR auto-finalise error:', err));

      clearPatientData();
      setSectionDirty(false);

      // Navigate to the Autism-specific documents dashboard (different route
      // from ADHD's /patient/{id}/documents).
      navigate(`/patient/${patientId}/adult-autism/documents`);
    } catch (error) {
      console.error('Error during document review completion:', error);
      // Surface the failure so the clinician knows the exit did not complete —
      // silently staying on the page looks like a frozen button.
      toast.error(
        'Could not save your review just now. Please check your connection and try again.',
        { toastId: 'review-exit-error' }
      );
    } finally {
      setIsSaving(false);
    }
  };

  // --------------------------------------------------------------------------
  // Scroll to First Unreviewed
  // --------------------------------------------------------------------------
  // Finds the first paragraph that hasn't been approved yet and scrolls to it,
  // then triggers a click so the side panel opens with its AI inputs.
  const goToFirstUnreviewed = () => {
    const allParagraphs = document.querySelectorAll('[data-paragraph-container]');
    const firstUnreviewed = Array.from(allParagraphs).find(p => !reviewedCodes.has(p.id));

    if (firstUnreviewed && documentContainerRef.current) {
      const containerRect = documentContainerRef.current.getBoundingClientRect();
      const nextRect = firstUnreviewed.getBoundingClientRect();

      const scrollTop = documentContainerRef.current.scrollTop +
        (nextRect.top - containerRect.top) -
        containerRect.height / 3;

      documentContainerRef.current.scrollTo({
        top: scrollTop,
        behavior: 'smooth',
      });

      setTimeout(() => {
        const nextCode = firstUnreviewed.id;
        const nextContent = REPORT_DATA_OUTPUTS[nextCode];

        handleParagraphClick(
          { currentTarget: firstUnreviewed },
          nextCode,
          nextContent
        );
      }, 300);
    }
  };

  // --------------------------------------------------------------------------
  // Progress Calculation
  // --------------------------------------------------------------------------
  const calculateProgress = useCallback(() => {
    if (totalParagraphs === 0) return 0;
    return Math.round((reviewedCodes.size / totalParagraphs) * 100);
  }, [reviewedCodes.size, totalParagraphs]);

  // --------------------------------------------------------------------------
  // Effects: Body overflow lock, model inputs hydration, scroll/click handlers
  // --------------------------------------------------------------------------

  // Lock body scroll while this full-screen review page is mounted
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  // Hydrate the model inputs (clinician notes, decisions, patient/informant prefill)
  // from the reportContent.history.inputs field into component state.
  // In ADHD this was read from patientDataModel.dataModel['6_manageReports'].sections['1'].inputs;
  // here it's directly on the history subcollection document.
  useEffect(() => {
    const inputs = historyDoc?.inputs;
    if (inputs) {
      setState(prev => ({ ...prev, modelInputsFirestore: inputs }));
    }
  }, [historyDoc]);

  // Ensure scroll remains enabled during edit mode
  useEffect(() => {
    const currentContainer = documentContainerRef.current;

    if (isEditing && currentContainer) {
      const originalOverflow = currentContainer.style.overflow;

      currentContainer.style.overflow = 'auto';

      const handleWheel = (e) => {
        e.stopPropagation();
      };

      currentContainer.addEventListener('wheel', handleWheel, { passive: true });

      return () => {
        currentContainer.removeEventListener('wheel', handleWheel);
        currentContainer.style.overflow = originalOverflow;
      };
    }
  }, [isEditing]);

  // Deselect the active paragraph when clicking on empty space (not on a
  // paragraph or the action panel). Ignored during edit mode.
  const handlePageClick = useCallback((event) => {
    if (isEditing) return;

    if (
      event.target.closest('[data-paragraph-container]') ||
      event.target.closest('[data-action-panel]')
    ) {
      return;
    }
    setIsEditing(false);
    setState(prev => ({
      ...prev,
      selectedCode: null,
      selectedContent: null,
      isSidePanelOpen: false,
    }));
  }, [isEditing]);

  useEffect(() => {
    const container = documentContainerRef.current;

    if (container) {
      container.addEventListener('click', handlePageClick);
    }

    return () => {
      if (container) {
        container.removeEventListener('click', handlePageClick);
      }
    };
  }, [handlePageClick]);

  // --------------------------------------------------------------------------
  // Render
  // --------------------------------------------------------------------------
  return (
    <Layout activePage="dashboard" scrollable={false}>
      <InitialWarningDialog showInitialWarning={showInitialWarning} setShowInitialWarning={setShowInitialWarning} />
      <div className="h-full flex flex-col overflow-hidden  ">

        {/* Dim overlay during edit mode — pointer-events: none allows clicks through */}
        {isEditing && (
          <div
            className="fixed inset-0 bg-gray-900/20 z-10 transition-opacity duration-"
            style={{
              pointerEvents: 'none',
            }}
          />
        )}

        {/* Fixed header section with progress bar and action buttons */}
        <div className="flex-none pt-4">
          <DocumentReviewHeader
            title={`Reviewing: ${MODULE_TITLE}`}
            reviewedCodes={reviewedCodes}
            totalParagraphs={totalParagraphs}
            isSaving={isSaving}
            handleExitReview={handleExitReview}
            goToFirstUnreviewed={goToFirstUnreviewed}
            calculateProgress={calculateProgress}
          />
          <WarningBar show={showWarning} />
          {!canEdit && header && (
            <div className="mt-2 bg-gray-100 border border-gray-300 rounded-lg px-4 py-2 text-sm text-gray-700">
              Read-only view. Only{assignedClinicianName ? ` ${assignedClinicianName}, the clinician` : ' the clinician'} assigned to the History module can review, edit, and approve these paragraphs. You can still preview and download the report from the Documents dashboard.
            </div>
          )}
        </div>

        {/* Scrollable document section with side panel */}
        <div className="flex-1 px-8 pb-8 min-h-0 relative">
          <div className="document-display-container h-full overflow-hidden bg-slate-800">
            <div className="flex h-full">
              {/* Left side — Document container with paragraphs */}
              <div
                ref={documentContainerRef}
                className={`
                  w-1/2 relative m-4 p-16 border shadow-md bg-white
                  ${isEditing ? 'overflow-hidden' : 'overflow-y-auto'}
                `}
              >
                <div className="space-y-8 text-left">
                  {Object.entries(docStructure).map(([title, paragraphs]) => (
                    <Section
                      key={title}
                      title={title}
                      paragraphs={paragraphs}
                    />
                  ))}
                </div>
                <div className="h-[calc(100vh-32rem)]" aria-hidden="true" />

                {/* Floating Action Panel — positioned beside the selected paragraph */}
                <FloatingActionPanel
                  panelRef={panelRef}
                  position={panelPosition}
                  visible={canEdit && Boolean(state.selectedCode)}
                  isEditing={isEditing}
                  hasBeenModified={hasBeenModified(state.selectedCode)}
                  onStartEdit={handleStartEdit}
                  onSave={handleSaveEdit}
                  onCancel={handleCancel}
                  onReset={handleReset}
                  onApprove={handleMarkAsDone}
                  onSkip={handleSkip}
                />
              </div>

              {/* Right side — AI Inputs side panel */}
              <div className={`
                  w-2/5 p-4 flex flex-col
                  ${!state.selectedCode ? 'hidden' : ''}
                `}>
                <div className={`
                    flex-1 text-sm bg-gray-100 rounded-xl overflow-y-auto 
                    border shadow-sm
                    flex flex-col h-full
                  `}>
                  {state.selectedCode && (
                    <div className="flex flex-col h-full p-6 overflow-hidden">
                      <div className="flex-1 flex flex-col min-h-0">
                        <div className="flex-none flex justify-between items-center">
                          <p className="text-xl font-bold text-gray-800 mb-2">AI Inputs</p>
                        </div>
                        <div className="overflow-y-auto">
                          {renderModelInput("Your decisions", state.clinicianDecisions)}
                          {renderModelInput("Your notes", state.clinicianInputs)}
                          {renderModelInput("Patient's report", state.patientInputs)}
                          {renderModelInput("Informant comments", state.informantInputs)}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
